class SilentError extends Error {
    isSilent = true;
}

export class PromiseUtils {
    /**
     * 保证一个表达式 `expr` 为真, 并返回表达式的值
     * @example
     *   PromiseUtils.assert(qty === 4, '数量必须为4');
     * @param expr {any} 表达式
     * @param msg {string} 失败时消息
     * @returns {any} 返回 表达式的值
     */
    static assert(expr, msg) {
        if (!expr) {
            throw new Error(msg);
        }
        return expr;
    }

    static assertNot(expr, msg) {
        if (expr) {
            throw new Error(msg);
        }
        return expr;
    }

    /**
     * 返回一个单参数`param`的函数`fnToReturn`, 改函数返回不经改变的参数`param`;
     * 保证一个函数`fn`返回真，`fn`函数的参数使用`fnToReturn` 的参数；
     * @example
     *   somePromise
     *       .then(PromiseUtils.assertFn(response => response.code === '0', '接口返回失败'))
     *       .then(...)
     * @param fn {(param) => Boolean} 验证函数，返回 Boolean值
     * @param msg {string}
     * @returns {(param) => param}
     */
    static assertFn(fn, msg) {
        return (param) => {
            if (!fn(param)) {
                throw new Error(msg);
            }
            return param;
        };
    }

    /**
     * 返回一个单参数`param`的函数`fnToReturn`, 改函数返回不经改变的参数`param`;
     * 调用一个函数`fn`，`fn`函数的参数使用`fnToReturn` 的参数；
     * @example
     *   somePromise
     *       .then(PromiseUtils.do(response => response.code === '0'))
     *       .then(...)
     * @param fn
     * @returns {function(*=): *}
     */
    static do(fn) {
        return (params) => {
            fn(params);
            return params;
        };
    }

    /**
     * 保证表达式 `expr` 的值为真，并返回该值，但不报错。
     * @param expr
     * @returns {*}
     */
    static assertSilently(expr) {
        if (!expr) {
            throw new SilentError('');
        }
        return expr;
    }

    /**
     * 返回不报错的 Error
     * @returns {SilentError}
     */
    static silentError() {
        return new SilentError('');
    }

    /**
     * 静悄悄地失败
     */
    static failSilently() {
        throw new SilentError('');
    }

    /**
     * 确保接口返回成功, 透传 `res`
     * @param res
     * @param msg
     * @returns {*}
     */
    static assertResponseSuccess(res, msg) {
        if (!res || +res.code !== 0) {
            throw new SilentError('');
        }
        return res;
    }

    /**
     * 确保接口返回成功且带有 data, 透传 `res`
     * @returns {Generator<*, void, *>}
     */
    static assertResponseSuccessWithData(res, msg) {
        if (!res || +res.code !== 0 || (res.data === null || res.data === undefined)) {
            throw new SilentError((res && res.msg) || msg);
        }
        return res;
    }

    /**
     * 构建 messenger
     * @param msgr
     * @returns {{e(*, *): void, w(*, *): void}}
     */
    static buildErrorMessenger(msgr) {
        return {
            w(error, msg) {
                if (!error.isSilent) {
                    msgr.warning(error.message || msg);
                }
                if (localStorage.getItem('__DEBUG__')) {
                    console.trace(error); // eslint-disable-line
                }
            },
            e(error, msg) {
                if (!error.isSilent) {
                    msgr.error(error.message || msg);
                }
                if (localStorage.getItem('__DEBUG__')) {
                    console.trace(error); // eslint-disable-line
                }
            }
        };
    }
}
